import { useEffect, useRef, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { PanelLeftClose } from "lucide-react";
import { hubHosted } from "lib/hubSso";
import { ALL_MENU_ITEMS } from "constants/common";
import useCheckPermission from "hooks/useCheckPermission";
import { PERMISSIONS } from "constants/common";

const BUILD_VERSION = "v2.4.1";

const getNavigations = (userRole) => {
  const role = (userRole || "").toLowerCase().replace(/\s+/g, "_");
  const visible = ALL_MENU_ITEMS.filter((item) => !item.hideInSidebar);
  if (!role) return visible;
  const filtered = visible.filter((item) => item.roles.includes(role));
  return filtered.length > 0 ? filtered : visible;
};

export default function Sidebar() {
  const checkPermission = useCheckPermission();
  // Under the Hub the rail is part of the suite's chrome: dark navy continuing
  // straight down from the Hub header, teal selection — exactly the rail
  // xcess360 and xref360 draw, reading the Hub's published tokens with the
  // same literals as fallbacks. Standalone keeps QBuy360's own light sidebar.
  const embedded = hubHosted();
  /* Embedded, the rail behaves the way every other tool's does.

     <semi360-rail> — what the Hub, xcess360 and xref360 draw — rests at its
     mini width and opens to full only while the pointer is on it, collapsing
     again on the way out. This sidebar had the mini rest state but no peek, so
     it was the one rail in the suite that never opened: the labels were
     unreachable without clicking the collapse button, and it read as stuck.

     The timings are the shared rail's own (rail-model.ts createPeek): 120ms
     before opening, so crossing the rail on the way somewhere else does not
     flash it, and 250ms before closing, so clipping the edge on the way to a
     row does not snatch it away.

     Standalone keeps QBuy360's own light sidebar, expanded, with the collapse
     button as the only control — no Hub chrome, no peek. */
  const [peek, setPeek] = useState(false);
  const [manual, setManual] = useState(null);   // null = follow the rail's rules
  const isCollapsed = manual != null ? manual : embedded && !peek;

  const peekTimer = useRef(null);
  useEffect(() => () => clearTimeout(peekTimer.current), []);
  const schedulePeek = (open) => {
    if (!embedded || manual != null) return;
    clearTimeout(peekTimer.current);
    peekTimer.current = setTimeout(() => setPeek(open), open ? 120 : 250);
  };
  const location = useLocation();
  const user = useSelector((state) => state?.user?.user);
  const userRole = user?.company_role;
  const navigation = getNavigations(userRole);

  const isActive = (href) => {
    const currentPath = location?.pathname || "/";
    if (href === "/") return currentPath === "/";
    return currentPath.startsWith(href);
  };

  const getMenuAccessKey = (item) => item.href.replace(/^\//, "").replace(/-/g, "_");

  return (
    <aside
      onMouseEnter={() => schedulePeek(true)}
      onMouseLeave={() => schedulePeek(false)}
      onFocusCapture={() => schedulePeek(true)}
      onBlurCapture={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) schedulePeek(false);
      }}
      className={`flex flex-col py-3 px-2 gap-0.5 overflow-y-auto transition-[width] duration-300 ease-in-out shrink-0 ${
        embedded
          ? "bg-[var(--brand-ink,#191334)] border-r border-white/10"
          : "bg-white border-r border-s360-stroke1"
      } ${
        isCollapsed
          ? "w-[var(--product-rail-mini-w,60px)]"
          : "w-[var(--product-rail-w,238px)]"
      }`}
    >
      {!isCollapsed && (
        <div
          className={`text-[11px] font-semibold uppercase tracking-[0.08em] px-2.5 pt-3.5 pb-1.5 ${
            embedded ? "text-[#b4c7c4]" : "text-s360-fg3"
          }`}
        >
          Workspace
        </div>
      )}

      <nav className="flex flex-col gap-0.5">
        {navigation.map((item) => {
          const Icon = item.icon;
          const isVisible = checkPermission(getMenuAccessKey(item), PERMISSIONS.VIEW);
          if (!isVisible) return null;
          const active = isActive(item.href);
          return (
            <Link
              key={item.name}
              to={item.href}
              title={isCollapsed ? item.name : undefined}
              className={`relative flex items-center gap-2.5 px-2.5 py-2 rounded text-sm font-semibold transition-colors ${
                isCollapsed ? "justify-center" : ""
              } ${
                embedded
                  ? active
                    ? "bg-[#24D0C029] text-[#eaf3f1]"
                    : "text-[#b4c7c4] hover:bg-white/5 hover:text-[#eaf3f1]"
                  : active
                    ? "bg-s360-row-selected text-s360-violet-900"
                    : "text-s360-fg1 hover:bg-s360-bg2"
              }`}
            >
              {active && (
                <span
                  className={`absolute left-0 top-2 bottom-2 w-[2px] rounded-sm ${
                    embedded ? "bg-[var(--brand-teal,#24D0C0)]" : "bg-s360-violet"
                  }`}
                />
              )}
              <Icon
                className={`w-5 h-5 shrink-0 ${
                  embedded
                    ? active
                      ? "text-[var(--brand-teal,#24D0C0)]"
                      : "text-[#b4c7c4]"
                    : active
                      ? "text-s360-violet"
                      : "text-s360-fg3"
                }`}
              />
              {!isCollapsed && (
                <span className="whitespace-nowrap min-w-0 truncate">{item.name}</span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Footer: collapse + build version */}
      <div
        className={`mt-auto flex items-center gap-2 pt-2.5 pb-1 px-1.5 border-t ${
          embedded ? "border-white/10" : "border-s360-stroke1"
        } ${isCollapsed ? "justify-center" : ""}`}
      >
        <button
          type="button"
          onClick={() => { clearTimeout(peekTimer.current); setPeek(false); setManual(!isCollapsed); }}
          title={isCollapsed ? "Expand menu" : "Collapse menu"}
          aria-label={isCollapsed ? "Expand menu" : "Collapse menu"}
          className={`w-8 h-8 shrink-0 inline-flex items-center justify-center rounded-md border transition-colors ${
            embedded
              ? "border-white/10 text-[#b4c7c4] hover:bg-white/5 hover:text-[#eaf3f1]"
              : "border-s360-stroke1 text-s360-fg3 hover:bg-s360-bg2 hover:text-s360-violet hover:border-s360-violet-100"
          }`}
        >
          <PanelLeftClose
            className={`w-[18px] h-[18px] transition-transform ${isCollapsed ? "rotate-180" : ""}`}
          />
        </button>
        {!isCollapsed && (
          <span className="font-mono text-[10px] text-s360-fg-disabled truncate">{BUILD_VERSION}</span>
        )}
      </div>
    </aside>
  );
}
